import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ModuleSummary } from '@shared/types';
import {
  activateModule,
  createModule,
  deleteModule,
  fetchModuleById,
  fetchUserModulesPage,
  makeModulePrivate,
  makeModulePublic,
  pauseModule,
  restoreModule,
  type CreateModuleError,
  type CreateModulePayload,
  type UserModulesPage,
  type UserModulesQueryArgs,
} from '../api/moduleApi';

export const moduleKeys = {
  list: (args: UserModulesQueryArgs) => ['modules', 'list', args] as const,
  listsRoot: ['modules', 'list'] as const,
  byId: (id: string) => ['modules', id] as const,
};

export function useUserModulesPageQuery(
  args: UserModulesQueryArgs,
  options: { enabled?: boolean } = {},
) {
  return useQuery<UserModulesPage, Error>({
    queryKey: moduleKeys.list(args),
    queryFn: () => fetchUserModulesPage(args),
    enabled: options.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
}

export function useModuleByIdQuery(id: string | null | undefined) {
  return useQuery({
    queryKey: moduleKeys.byId(id ?? ''),
    queryFn: () => fetchModuleById(id!),
    enabled: !!id,
  });
}

function shouldKeepInFilter(
  status: UserModulesQueryArgs['status'] | undefined,
  m: ModuleSummary,
): boolean {
  if (status === 'deleted') return m.deleted;
  if (status === 'active') return m.active && !m.deleted;
  if (status === 'paused') return !m.active && !m.deleted;
  // No status filter → "Все" (deleted are filtered out client-side)
  return !m.deleted;
}

function patchModuleInCaches(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
  patch: Partial<ModuleSummary>,
) {
  qc.setQueryData<ModuleSummary | null>(moduleKeys.byId(id), (old) =>
    old ? { ...old, ...patch } : old,
  );

  const cached = qc.getQueriesData<UserModulesPage>({ queryKey: moduleKeys.listsRoot });
  for (const [key, data] of cached) {
    if (!data) continue;
    const existing = data.items.find((m) => m.id === id);
    if (!existing) continue;
    const updated = { ...existing, ...patch };
    const args = (key[2] ?? {}) as UserModulesQueryArgs;
    if (shouldKeepInFilter(args.status, updated)) {
      qc.setQueryData<UserModulesPage>(key, {
        ...data,
        items: data.items.map((m) => (m.id === id ? updated : m)),
      });
    } else {
      qc.setQueryData<UserModulesPage>(key, {
        ...data,
        items: data.items.filter((m) => m.id !== id),
        total: Math.max(0, data.total - 1),
      });
    }
  }
}

export interface ToggleModulePayload {
  id: string;
  active: boolean;
}

export interface ToggleVisibilityPayload {
  id: string;
  isPublic: boolean;
}

export function useToggleVisibilityMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, ToggleVisibilityPayload>({
    mutationFn: ({ id, isPublic }) => (isPublic ? makeModulePublic(id) : makeModulePrivate(id)),
    onMutate: async ({ id, isPublic }) => {
      await qc.cancelQueries({ queryKey: moduleKeys.listsRoot });
      patchModuleInCaches(qc, id, { isPublic });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    },
  });
}

export function useToggleModuleMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, ToggleModulePayload>({
    mutationFn: ({ id, active }) => (active ? activateModule(id) : pauseModule(id)),
    onMutate: async ({ id, active }) => {
      await qc.cancelQueries({ queryKey: moduleKeys.listsRoot });
      patchModuleInCaches(qc, id, { active });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    },
  });
}

export function useRestoreModuleMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: restoreModule,
    onSuccess: (_data, id) => {
      patchModuleInCaches(qc, id, { deleted: false, active: true });
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
      qc.removeQueries({ queryKey: moduleKeys.byId(id) });
    },
  });
}

export function useDeleteModuleMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteModule,
    onSuccess: (_data, id) => {
      patchModuleInCaches(qc, id, { deleted: true, active: false });
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
      qc.removeQueries({ queryKey: moduleKeys.byId(id) });
    },
  });
}

export function useCreateModuleMutation() {
  const qc = useQueryClient();
  return useMutation<ModuleSummary, CreateModuleError, CreateModulePayload>({
    mutationFn: createModule,
    onSuccess: (created) => {
      qc.setQueryData<ModuleSummary>(moduleKeys.byId(created.id), created);
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    },
  });
}
